// @ts-nocheck
// W14a — Shell map-first para /finance/logradouro e /view/logradouro.
// Mesmo padrão do NavegadorShell: SmartMap montado uma vez, sidebar swap via React Router
// <Outlet/>, search floating top-left, controles auto-stack bottom-right, altura dinâmica
// respeitando Breadcrumbs + Footer.
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { ActionIcon, Paper, TextInput, Tooltip } from "@mantine/core";
import { IconSearch, IconX } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";

import SmartMap, { mapKey } from "../Map/SmartMap";
import SmartMapLegend from "../Map/SmartMapLegend";
import * as logradouroActions from "../../actions/Logradouro";
import { LogradourosMapProvider, useLogradourosMap } from "./LogradourosMapContext";
// Reutiliza estilos do NavegadorShell — mesmo layout + auto-stack de controles
import "../Navegador/NavegadorShell.css";

export interface LogradourosShellProps {
  baseRoute: string;
  mapKeyValue?: string;
}

function useShouldShowSidebar(baseRoute: string): boolean {
  const location = useLocation();
  const trim = (s: string) => (s.length > 1 ? s.replace(/\/+$/, "") : s);
  const isExactBase = trim(location.pathname) === trim(baseRoute);
  const params = new URLSearchParams(location.search);
  const hasSearchParams = params.has("q") || params.has("e");
  return !isExactBase || hasSearchParams;
}

/** Search bar floating dentro do shell — TextInput simples; submit navega para ?q=... */
function ShellSearch({ baseRoute }: { baseRoute: string }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [value, setValue] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setValue(params.get("q") ?? "");
  }, [location.key]);

  const submit = () => {
    const params = new URLSearchParams({ page: "1" });
    const t = value.trim();
    if (t) params.set("q", t);
    navigate(`${baseRoute}?${params.toString()}`);
  };

  return (
    <TextInput
      style={{ width: "100%" }}
      placeholder="Nome do logradouro"
      value={value}
      maxLength={300}
      onChange={(e) => setValue(e.currentTarget.value)}
      onKeyDown={(e) => e.key === "Enter" && submit()}
      rightSection={
        <ActionIcon variant="filled" onClick={submit} title="Buscar">
          <IconSearch size={16} />
        </ActionIcon>
      }
    />
  );
}

function LogradourosShellInner({ baseRoute, mapKeyValue }: LogradourosShellProps) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { geoJson, smartMapApiRef, setSelectedId } = useLogradourosMap();

  const showSidebar = useShouldShowSidebar(baseRoute);
  const resolvedMapKey = mapKeyValue ?? mapKey.LOGRADOUROS;

  // Altura dinâmica (igual NavegadorShell)
  const shellRef = useRef<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    const node = shellRef.current;
    if (!node) return;
    const FOOTER_H = 22;
    const update = () => {
      const top = node.getBoundingClientRect().top;
      const h = Math.max(window.innerHeight - top - FOOTER_H, 240);
      node.style.height = `${h}px`;
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(document.body);
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, []);

  // Auto-stack dos controles OL (igual NavegadorShell)
  useLayoutEffect(() => {
    const node = shellRef.current;
    if (!node) return;
    const SELECTORS_TOP_TO_BOTTOM = [
      ".btn-map-info",
      ".map-custom-tools",
      ".btn-measure",
      ".ol-zoom",
    ];
    const GAP = 8;
    const BASE = 12;
    let scheduled = false;
    const arrange = () => {
      scheduled = false;
      let bottom = BASE;
      for (let i = SELECTORS_TOP_TO_BOTTOM.length - 1; i >= 0; i--) {
        const el = node.querySelector(SELECTORS_TOP_TO_BOTTOM[i]) as HTMLElement | null;
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        if (rect.height === 0) continue;
        el.style.setProperty("position", "absolute", "important");
        el.style.setProperty("bottom", `${bottom}px`, "important");
        el.style.setProperty("top", "auto", "important");
        bottom += rect.height + GAP;
      }
    };
    const scheduleArrange = () => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(arrange);
    };
    scheduleArrange();
    const obs = new MutationObserver(scheduleArrange);
    obs.observe(node, { childList: true, subtree: true });
    window.addEventListener("resize", scheduleArrange);
    return () => {
      obs.disconnect();
      window.removeEventListener("resize", scheduleArrange);
    };
  }, []);

  // Click identify no mapa: 0 → toast; 1 → /visualizar/:id; >1 → ?e=...
  const handleIdentifyOnExtent = useCallback(
    (geographicExtent: number[]) => {
      dispatch(
        (logradouroActions as any).identifyOnExtent(geographicExtent, ({ data }: any) => {
          if (!data || data.length === 0) {
            notifications.show({ color: "yellow", message: "Nenhum logradouro encontrado." });
          } else if (data.length === 1) {
            navigate(`${baseRoute}/visualizar/${data[0]}`);
          } else {
            const e = JSON.stringify(geographicExtent).slice(1, -1);
            navigate(`${baseRoute}?e=${e}`);
          }
        }),
      );
    },
    [baseRoute, navigate, dispatch],
  );

  const handleCloseSidebar = () => {
    setSelectedId(null);
    navigate(baseRoute);
  };

  return (
    <div ref={shellRef} className="navegador-shell">
      <div className="navegador-shell__map">
        <SmartMap
          mapKey={resolvedMapKey}
          onApiReady={(api) => {
            smartMapApiRef.current = api;
          }}
          geoJson={geoJson}
          identifyOnExtent={handleIdentifyOnExtent}
          identifyOnPixelTolerance={5}
        />
        <SmartMapLegend className="navegador-shell__legend" />
      </div>

      <div className="navegador-shell__search">
        <Paper shadow="md" radius="md" withBorder p={4}>
          <ShellSearch baseRoute={baseRoute} />
        </Paper>
      </div>

      {showSidebar && (
        <Paper shadow="md" radius="md" className="navegador-shell__sidebar" withBorder>
          <div className="navegador-shell__sidebar-close">
            <Tooltip label="Fechar e voltar ao mapa">
              <ActionIcon
                variant="subtle"
                onClick={handleCloseSidebar}
                aria-label="Fechar sidebar"
              >
                <IconX size={18} />
              </ActionIcon>
            </Tooltip>
          </div>
          <div className="navegador-shell__sidebar-body">
            <Outlet />
          </div>
        </Paper>
      )}
    </div>
  );
}

export default function LogradourosShell(props: LogradourosShellProps) {
  return (
    <LogradourosMapProvider>
      <LogradourosShellInner {...props} />
    </LogradourosMapProvider>
  );
}
